import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { apiEndpoints, apiRequest } from '@/config/api';
import { useEffect } from 'react';
import SeoHead, { companySchema } from '@/components/SeoHead';

const schema = z.object({
  name: z.string().trim().min(2, 'نام الزامی است').max(200),
  phone: z.string().trim().max(50).optional().or(z.literal('')),
  email: z.string().trim().email('ایمیل نامعتبر').max(200).optional().or(z.literal('')),
  subject: z.string().trim().max(250).optional().or(z.literal('')),
  message: z.string().trim().min(5, 'متن پیام الزامی است').max(5000),
}).refine((d) => !!d.email || !!d.phone, { message: 'ایمیل یا تلفن الزامی است', path: ['email'] });

const Contact = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      parsed.error.errors.forEach((er) => { errs[er.path[0] as string] = er.message; });
      setErrors(errs);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await apiRequest(apiEndpoints.contacts, { method: 'POST', body: JSON.stringify(parsed.data) });
      toast.success('پیام شما با موفقیت ارسال شد');
      setForm({ name: '', phone: '', email: '', subject: '', message: '' });
    } catch (err: any) {
      toast.error(err.message || 'خطا در ارسال پیام');
    } finally { setLoading(false); }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SeoHead
        title="تماس با کیا الکتروموتور پارت | مشاوره و استعلام"
        description="برای مشاوره، استعلام قیمت و دریافت اطلاعات محصولات با کیا الکتروموتور پارت در صفادشت تماس بگیرید."
        canonicalPath="/contact"
        keywords={['تماس با کیا الکتروموتور', 'استعلام قیمت الکتروموتور', 'مشاوره الکتروموتور', 'صفادشت']}
        structuredData={{ '@context': 'https://schema.org', '@type': 'ContactPage', name: 'تماس با کیا الکتروموتور پارت', about: companySchema, mainEntity: companySchema }}
      />
      <Header />
      <main className="pt-14 sm:pt-16 md:pt-20">
        <section className="py-16 bg-gradient-to-b from-steel-dark to-steel text-white">
          <div className="container mx-auto px-4 text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-5xl font-bold mb-3">تماس با ما</motion.h1>
            <p className="text-white/80 max-w-2xl mx-auto">برای دریافت مشاوره، استعلام قیمت یا هرگونه سوال با ما در ارتباط باشید.</p>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white dark:bg-steel-dark/40 p-6 md:p-8 rounded-2xl shadow-lg border border-copper/10">
              <h2 className="text-2xl font-bold mb-6">ارسال پیام</h2>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Input placeholder="نام و نام خانوادگی *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="text-right" />
                    {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Input placeholder="شماره تماس" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} dir="ltr" />
                    {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <Input placeholder="ایمیل" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} dir="ltr" />
                    {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Input placeholder="موضوع" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="text-right" />
                  </div>
                </div>
                <div>
                  <Textarea placeholder="متن پیام *" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="text-right" />
                  {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
                </div>
                <Button type="submit" disabled={loading} className="bg-copper hover:bg-copper-dark text-white px-8">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Send className="w-4 h-4 ml-2" />}
                  ارسال پیام
                </Button>
              </form>
            </div>

            <div className="space-y-4">
              <div className="bg-copper/10 p-6 rounded-2xl">
                <div className="flex items-start gap-3 mb-4"><Phone className="w-5 h-5 text-copper mt-1" /><div><p className="font-bold">تلفن</p><p className="text-steel" dir="ltr">021-91009883</p><p className="text-steel" dir="ltr">021-65028992</p></div></div>
                <div className="flex items-start gap-3 mb-4"><Mail className="w-5 h-5 text-copper mt-1" /><div><p className="font-bold">ایمیل</p><p className="text-steel" dir="ltr">info@kiaelectromotor.com</p></div></div>
                <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-copper mt-1" /><div><p className="font-bold">آدرس</p><p className="text-steel text-sm leading-relaxed">صفادشت، شهرک صنعتی، خیابان دهم غربی پلاک ۱۶۴</p></div></div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
